/**
 * Editorial source-of-truth for the Insights section + dedicated
 * `/insights` page. These are the articles drafted for launch — they
 * live in code (not a CMS) until a CMS exists.
 *
 * Cadence: the brief calls for one new article per month. The four
 * pieces below cover the cornerstone topics every Hive Homes prospect
 * eventually asks about (market direction, Golden Visa, lagoon
 * communities, RERA). When the team publishes a new monthly piece,
 * prepend it to INSIGHTS and the homepage / archive pick it up
 * automatically.
 */

export type InsightCategory =
  | "Market Report"
  | "Investor Guide"
  | "Neighbourhood Guide"
  | "Regulatory"
  | "Hive Homes News";

export interface InsightSection {
  /** Optional subheading inside an article. */
  heading?: string;
  /** Plain paragraphs of body copy. */
  body: string[];
}

export interface InsightArticle {
  slug: string;
  category: InsightCategory;
  title: string;
  excerpt: string;
  /** Cover image URL (Dubai photography) for cards + the article hero. */
  image: string;
  /** ISO date string (yyyy-mm-dd). */
  date: string;
  /** Estimated reading time in minutes. */
  readMinutes: number;
  /** Article body — ordered list of sections. */
  sections: InsightSection[];
  ctaLabel: string;
  seoTitle: string;
  seoDescription: string;
}

export const INSIGHTS: InsightArticle[] = [
  {
    slug: "dubai-property-market-q2-2026",
    category: "Market Report",
    title: "Dubai Property Market Q2 2026 — What Buyers Need to Know",
    excerpt:
      "Volumes held firm into Q1 and pricing has decoupled by community. Here is where the value is right now, where competition is heaviest, and how we are advising buyers entering this summer.",
    image: "/images/site/insights.jpg",
    date: "2026-05-12",
    readMinutes: 6,
    sections: [
      {
        body: [
          "Dubai entered 2026 with the same momentum it carried out of 2025. Transaction volumes in Q1 settled just below the all-time Q1 peak set last year — high enough to confirm the market is no longer a recovery story, but no longer accelerating vertically the way it did between 2022 and 2024. The shift matters for buyers: the easy decisions of the last cycle have given way to a market where community-level pricing has decoupled, and where the discipline of buying right is what will define the next three to five years.",
        ],
      },
      {
        heading: "The headline numbers",
        body: [
          "Q1 2026 closed with roughly 50,000 sales transactions city-wide, with off-plan continuing to account for the larger share of volume. Average ticket size held in the low-to-mid AED 2 millions, reflecting the same mix of premium villas pulling the average up and JVC-style apartments pulling it down that we saw in 2025. Rental indices continued to lift in the second-tier communities and softened modestly in the most over-supplied apartment districts — exactly the rebalancing we have been telling clients to expect.",
        ],
      },
      {
        heading: "Where the value sits today",
        body: [
          "Jumeirah Village Circle remains the most-asked-about community in our enquiry log, and for good reason. The right asset, well-finished and in a serviced building, will still deliver gross yields between 7% and 9% net of service charges, and there is enough inventory turning over that buyers don't need to overpay to compete. Business Bay continues to perform in the studio and one-bedroom band, where canal-facing stock is genuinely scarce. For families, Tilal Al Ghaf and The Valley remain our top picks for outdoor space at the price — both are seeing competitive bidding on the better plots.",
        ],
      },
      {
        heading: "Off-plan: more selective than 2025",
        body: [
          "Off-plan continues to drive headline transaction counts, but the field has widened — there are more launches every month than the demand can comfortably absorb, and a meaningful share of secondary off-plan inventory is now trading at or below original launch prices. We have shifted our advisory posture accordingly: we will only recommend off-plan when the developer has a credible delivery track record, the payment plan is genuinely buyer-friendly, and the launch price stacks up against equivalent ready stock.",
        ],
      },
      {
        heading: "Our recommendation",
        body: [
          "If you are buying in Dubai in 2026, the market is active without being frenzied. Well-priced properties still move inside a fortnight; mispriced ones sit. Our team is on the ground across every community we cover, and we are happy to share recent comparables and our view on individual buildings. Message us directly on WhatsApp for personalised advice.",
        ],
      },
    ],
    ctaLabel: "Message Our Team",
    seoTitle:
      "Dubai Property Market Q2 2026 — Buyer's Guide | Hive Homes Insights",
    seoDescription:
      "Dubai property market Q2 2026: where prices are decoupling, which communities offer the best value, and how Hive Homes is advising buyers entering the market this summer.",
  },
  {
    slug: "uae-golden-visa-real-estate-2026",
    category: "Investor Guide",
    title: "The UAE Golden Visa Through Real Estate — Complete 2026 Guide",
    excerpt:
      "A AED 2M+ property purchase still qualifies you for UAE residency. We break down exactly how the route works in 2026, which properties qualify, the family sponsorship rules, and the full process end-to-end.",
    image: "/images/site/property.jpg",
    date: "2026-04-08",
    readMinutes: 7,
    sections: [
      {
        body: [
          "The UAE Golden Visa changed the conversation around Dubai property when it was introduced, and three years on it has become the single most common reason international buyers cross our threshold. For anyone willing to purchase at AED 2 million or above, the visa opens the door to a 10-year UAE residency — renewable, extendable to family, and entirely independent of employment.",
        ],
      },
      {
        heading: "What qualifies in 2026",
        body: [
          "The property must be valued at AED 2 million or above. Ready and off-plan both count, provided the purchase value meets the threshold; for off-plan, the visa is typically issued against the registered Oqood once at least a portion of the purchase price is paid. Mortgaged purchases also qualify, subject to a minimum equity contribution — we can run the maths on any specific property.",
        ],
      },
      {
        heading: "What the visa actually gives you",
        body: [
          "A 10-year UAE residency, renewable indefinitely as long as you continue to own the qualifying property. You can sponsor your spouse, children of any age, and in defined circumstances your parents. You retain the visa even if you spend significant time outside the UAE — there is no minimum-stay requirement, unlike most other long-term residencies in the region.",
        ],
      },
      {
        heading: "The end-to-end process",
        body: [
          "The visa application runs in parallel with the property purchase. Once the sale is registered with the Dubai Land Department and the title deed (or Oqood, for off-plan) is issued, the application goes to the Federal Authority for Identity, Citizenship, Customs and Port Security. Typical processing time is two to four weeks. Our team works alongside legal partners who handle these applications regularly, so the property paperwork and the visa paperwork move together rather than sequentially.",
        ],
      },
      {
        heading: "What to watch out for",
        body: [
          "The valuation that counts is the price actually paid (or contractually committed for off-plan), not the developer's quoted list price or a future market valuation. Multiple properties can be combined to reach the threshold provided each individual title deed is in the same name. And — importantly — selling the qualifying property without replacing it will eventually end the visa, so plan ahead if you expect to recycle capital.",
        ],
      },
    ],
    ctaLabel: "Ask Us About the Golden Visa",
    seoTitle:
      "UAE Golden Visa Through Real Estate 2026 — Complete Guide | Hive Homes",
    seoDescription:
      "How to qualify for the UAE 10-year Golden Visa with a AED 2M+ Dubai property purchase in 2026. Eligible properties, family sponsorship, mortgaged purchases, and the full application process.",
  },
  {
    slug: "tilal-al-ghaf-vs-damac-lagoons",
    category: "Neighbourhood Guide",
    title:
      "Tilal Al Ghaf vs Damac Lagoons — Which Lagoon Community Is Right for You?",
    excerpt:
      "Two of Dubai's most popular family communities, both built around water. We compare pricing, lifestyle, developer reputation, handover timelines, and investment potential in 2026.",
    image: "/images/communities/damac-lagoons.jpg",
    date: "2026-03-04",
    readMinutes: 6,
    sections: [
      {
        body: [
          "Dubai's lagoon communities have captured the imagination of buyers looking for something genuinely different — a resort lifestyle, open space, and a sense of community. Two names come up again and again: Tilal Al Ghaf and Damac Lagoons.",
        ],
      },
      {
        heading: "The Developer",
        body: [
          "Tilal Al Ghaf is developed by Majid Al Futtaim. Their delivery track record is strong and the quality finish has been consistently well-received. Damac Lagoons is by DAMAC Properties, one of Dubai's most prolific developers with a large portfolio across the city.",
        ],
      },
      {
        heading: "The Lifestyle",
        body: [
          "Tilal Al Ghaf centres on a 70,000 sqm lagoon with a private beach, water sports, and over 30 hectares of green space. Damac Lagoons takes inspiration from Mediterranean Europe — each cluster themed around a different destination. Both prioritise outdoor, family-friendly living.",
        ],
      },
      {
        heading: "Pricing in 2026",
        body: [
          "Tilal Al Ghaf townhouses are now trading at roughly AED 3.5M to 5M on the secondary market, with the standalone villas comfortably above AED 8M. Damac Lagoons townhouses remain the more accessible entry point at AED 2M to 3.5M, with the larger villas in the AED 5M to 7M band. Both communities have appreciated meaningfully since launch, but the rate of appreciation is normalising in 2026 — buyers expecting another vertical leg up from here are likely to be disappointed.",
        ],
      },
      {
        heading: "Handover and resale dynamics",
        body: [
          "Tilal Al Ghaf is largely handed over with the secondary market now well-established, which means you can view a finished home and the comparables you trade against are real. Damac Lagoons is still in a handover-in-progress phase across several clusters — that creates short-term arbitrage opportunities for buyers willing to take a position before completion, but it also means resale liquidity is less reliable than at Tilal Al Ghaf.",
        ],
      },
      {
        heading: "Our view",
        body: [
          "For the strongest long-term proposition and the easiest exit, Tilal Al Ghaf leads. For access to a lagoon lifestyle at a materially lower entry price — and for buyers comfortable with the trade-offs of a community still finishing — Damac Lagoons is compelling. Our team has walked both repeatedly and can be opinionated about specific clusters. Message us for a personal recommendation.",
        ],
      },
    ],
    ctaLabel: "Get a Personal Recommendation",
    seoTitle:
      "Tilal Al Ghaf vs Damac Lagoons — Dubai Lagoon Community Comparison | Hive Homes",
    seoDescription:
      "Tilal Al Ghaf or Damac Lagoons? Compare developer reputation, pricing, lifestyle, and investment potential of Dubai's two leading lagoon communities.",
  },
  {
    slug: "rera-dubai-explained",
    category: "Regulatory",
    title: "RERA Dubai Explained — What Every Property Buyer Needs to Know",
    excerpt:
      "What is RERA, how does it protect you, and how do you verify in two minutes that the agent you are working with is properly licensed?",
    image: "/images/communities/zabeel.jpg",
    date: "2026-02-03",
    readMinutes: 5,
    sections: [
      {
        body: [
          "Buying property in Dubai is a well-regulated process — meaningfully more so than most international buyers expect. At the centre of that framework is RERA, the Real Estate Regulatory Agency, which sits under the Dubai Land Department and is the single most important name to understand before you sign anything.",
        ],
      },
      {
        heading: "What is RERA?",
        body: [
          "RERA is the regulatory arm of the Dubai Land Department. Every licensed agent, brokerage, and developer operating in Dubai is required to register with RERA, and every transaction in the city ultimately settles against a record RERA polices.",
        ],
      },
      {
        heading: "What it actually does for you as a buyer",
        body: [
          "RERA requires that every individual agent and every brokerage holds a current licence; that off-plan developers hold buyer payments in escrow accounts so your money is ring-fenced if a project is delayed or cancelled; that the agency-buyer relationship is formalised through standard Forms (most commonly Form B, F and I) before any commission is owed; and that every transaction is registered with the Dubai Land Department to be legally valid. None of these protections are optional — they apply whether you bought through a small boutique like us or one of the international portals.",
        ],
      },
      {
        heading: "How to verify an agent in two minutes",
        body: [
          "Every RERA-registered agent has a permit card with a photo, name, brokerage, and BRN (Broker Registration Number). You can verify any agent in seconds through the Dubai REST app, or by searching the BRN on the Dubai Land Department website. If an agent declines to share a BRN, walk away. At Hive Homes, our brokerage's RERA licence number is displayed in the footer of this website and on every piece of marketing we produce, and we will share individual advisors' BRNs on request without you having to ask twice.",
        ],
      },
    ],
    ctaLabel: "Speak to a RERA-Registered Advisor",
    seoTitle:
      "RERA Dubai Explained — What Every Property Buyer Needs to Know | Hive Homes",
    seoDescription:
      "What is RERA, how Dubai's real estate regulator protects buyers, escrow accounts for off-plan, and how to verify your agent's RERA registration.",
  },
];

export const INSIGHT_CATEGORIES: ("All" | InsightCategory)[] = [
  "All",
  "Market Report",
  "Neighbourhood Guide",
  "Investor Guide",
  "Regulatory",
  "Hive Homes News",
];

export function findInsight(slug: string | undefined): InsightArticle | undefined {
  if (!slug) return undefined;
  return INSIGHTS.find((a) => a.slug === slug);
}

export function formatInsightDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}
